import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { segmentSchema } from "../marketing/segments/segment-zod-schema";

export enum CustomerGroupType {
  STATIC = "static",
  DYNAMIC = "dynamic",
  PERSONA = "persona",
}

export const baseCustomerGroupSchema = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  name: z.string(),
  type: createEnum(CustomerGroupType, { enumName: "CustomerGroupType" }),
  count: z.number().optional(),
  smsSubscriberCount: z.number().optional(),
  emailSubscriberCount: z.number().optional(),
  emailEligibleCount: z.number().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const dynamicGroupSchema = baseCustomerGroupSchema.extend({
  type: z.literal(CustomerGroupType.DYNAMIC),
  conditions: segmentSchema,
  countsRefreshedAt: z.date().optional(),
});

export const staticGroupSchema = baseCustomerGroupSchema.extend({
  type: z.literal(CustomerGroupType.STATIC),
  isFullSubscriberList: z.boolean().optional(),
  isImportInProgress: z.boolean().optional(),
});

export const personaRubricSchema = z.object({
  // How they shop - timing, frequency, channels, research patterns (maps to customer shoppingBehavior)
  shoppingBehavior: z.string(),

  // What type of customer - loyalty level, engagement, purchase frequency (maps to customer customerType)
  customerType: z.string(),

  // What motivates their purchases - quality, price, convenience, brand (maps to customer valueDrivers)
  valueDrivers: z.string(),

  // Price sensitivity and spending level - budget vs premium, deal-seeking behavior
  priceProfile: z.string(),

  // Product categories and features they prefer - observable from purchase history
  productPreferences: z.string(),

  // Demographics and life context - age, income, location, family status from bio
  customerContext: z.string(),
});

export const personaSchema = z.object({
  name: z.string(),
  description: z.string(),
  emoji: z.string(),
  rubric: personaRubricSchema,
  confidence: z.number().min(1).max(100).nullish(),
});

/** Strict variant for OpenAI structured outputs (all fields required but nullable) */
export const personaStrictSchema = personaSchema.required();

export const personaGroupSchema = baseCustomerGroupSchema.extend({
  type: z.literal(CustomerGroupType.PERSONA),
  ...personaSchema.shape,
});

export const customerPersonaGroupSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  emoji: z.string(),
  description: z.string(),
  confidence: z.number().min(1).max(100).optional(),
  reasoning: z.string().optional(),
  addedAt: z.date(),
});

export type CustomerPersonaGroup = z.infer<typeof customerPersonaGroupSchema>;

export const customerGroupSchema = z.discriminatedUnion("type", [
  staticGroupSchema,
  dynamicGroupSchema,
  personaGroupSchema,
]);

export const minimalCustomerGroupSchema = z.object({
  _id: z.string(),
  name: z.string(),
  type: createEnum(CustomerGroupType, { enumName: "CustomerGroupType" }),
  countsRefreshedAt: z.date().optional(),
});

export type DynamicCustomerGroup = z.infer<typeof dynamicGroupSchema>;
export type StaticCustomerGroup = z.infer<typeof staticGroupSchema>;
export type PersonaCustomerGroup = z.infer<typeof personaGroupSchema>;
export type CustomerGroup = z.infer<typeof customerGroupSchema>;
export type MinimalCustomerGroup = z.infer<typeof minimalCustomerGroupSchema>;

// Persona assignment schemas
export const personaAssignmentSchema = z.object({
  personaId: z.string(),
  personaName: z.string(),
  confidence: z.number().min(1).max(100),
  reasoning: z.string(),
});

export const customerPersonaAssignmentResultSchema = z.object({
  customerId: z.string(),
  assignments: z.array(personaAssignmentSchema),
  overallReasoning: z.string(),
});

export type PersonaAssignment = z.infer<typeof personaAssignmentSchema>;
export type CustomerPersonaAssignmentResult = z.infer<
  typeof customerPersonaAssignmentResultSchema
>;
